import React, { useState } from 'react';
import { X, AlertTriangle, CheckCircle } from 'lucide-react';
import { ProjectStatus } from '../../types';

interface StatusUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (note?: string) => Promise<void>;
  currentStatus: ProjectStatus;
  newStatus: ProjectStatus;
  requiresNote: boolean;
  isLoading?: boolean;
}

const STATUS_LABELS: Record<ProjectStatus, string> = {
  [ProjectStatus.A_PREPARER]: 'À préparer',
  [ProjectStatus.SOUMIS]: 'Soumis',
  [ProjectStatus.ACCORD_PROVISOIRE]: 'Accord provisoire',
  [ProjectStatus.VALIDE]: 'Validé',
  [ProjectStatus.DEBLOCAGE_FONDS]: 'Déblocage fonds',
  [ProjectStatus.EN_COURS]: 'En cours',
  [ProjectStatus.VERIFICATION_FINALE]: 'Vérification finale',
  [ProjectStatus.TERMINE]: 'Terminé',
  [ProjectStatus.REJETE]: 'Rejeté',
  [ProjectStatus.SUSPENDU]: 'Suspendu'
};

export const StatusUpdateModal: React.FC<StatusUpdateModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  currentStatus,
  newStatus,
  requiresNote,
  isLoading = false
}) => {
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (requiresNote && !note.trim()) {
      setError('Une note explicative est requise pour ce statut');
      return;
    }

    if (requiresNote && note.trim().length < 10) {
      setError('La note doit contenir au moins 10 caractères');
      return;
    }

    try {
      await onConfirm(note.trim() || undefined);
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la mise à jour du statut');
    }
  };

  const getStatusColor = (status: ProjectStatus) => {
    if (status === ProjectStatus.REJETE || status === ProjectStatus.SUSPENDU) {
      return 'text-red-600';
    }
    if (status === ProjectStatus.TERMINE) {
      return 'text-green-600';
    }
    return 'text-blue-600';
  };

  const getModalIcon = () => {
    if (newStatus === ProjectStatus.REJETE || newStatus === ProjectStatus.SUSPENDU) {
      return <AlertTriangle className="w-6 h-6 text-red-600" />;
    }
    return <CheckCircle className="w-6 h-6 text-blue-600" />;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            {getModalIcon()}
            <h2 className="text-xl font-semibold text-gray-900">Confirmer le changement de statut</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Status Change Summary */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Changement de statut</h3>
            <div className="flex items-center space-x-2 text-sm">
              <span className="text-gray-600">De :</span>
              <span className="font-medium text-gray-900">"{STATUS_LABELS[currentStatus]}"</span>
              <span className="text-gray-400">→</span>
              <span className="text-gray-600">Vers :</span>
              <span className={`font-medium ${getStatusColor(newStatus)}`}>
                "{STATUS_LABELS[newStatus]}"
              </span>
            </div>
          </div>

          {/* Note */}
          <div>
            <label htmlFor="note" className="block text-sm font-medium text-gray-700 mb-2">
              {requiresNote ? 'Note explicative *' : 'Note (optionnel)'}
            </label>
            <textarea
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={
                requiresNote 
                  ? "Expliquez les raisons de ce changement de statut..."
                  : "Ajoutez un commentaire si nécessaire..."
              }
              required={requiresNote}
            />
            {requiresNote && (
              <p className="text-xs text-gray-500 mt-1">
                {note.length} / 10 caractères minimum
              </p>
            )}
          </div>

          {/* Warning for terminal states */}
          {(newStatus === ProjectStatus.REJETE || newStatus === ProjectStatus.SUSPENDU) && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-red-800">
                  <p className="font-medium">Attention</p>
                  <p>
                    {newStatus === ProjectStatus.REJETE 
                      ? 'Le projet sera marqué comme rejeté. Cette action est définitive.'
                      : 'Le projet sera suspendu. Vous pourrez le réactiver plus tard.'
                    }
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Notification Info */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Information :</strong> Le porteur du projet recevra automatiquement une notification 
              de ce changement de statut.
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading || (requiresNote && (!note.trim() || note.trim().length < 10))}
              className={`px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                newStatus === ProjectStatus.REJETE || newStatus === ProjectStatus.SUSPENDU
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Mise à jour...</span>
                </div>
              ) : (
                'Confirmer le changement'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};